import type { WebRtcMediaQuality } from '../types';
import type { SipLogger, SipTimerPort } from './contracts';

export const EMPTY_MEDIA_QUALITY: WebRtcMediaQuality = {
  active: false,
  updatedAt: null,
  codec: '',
  clockRate: null,
  channels: null,
  inboundBitrateKbps: null,
  outboundBitrateKbps: null,
  packetsReceived: 0,
  packetsSent: 0,
  packetsLost: 0,
  outboundPacketsLost: 0,
  packetLossPercent: null,
  outboundPacketLossPercent: null,
  jitterMs: null,
  outboundJitterMs: null,
  rttMs: null,
  jitterBufferMs: null,
  concealedSamples: 0,
  totalSamplesReceived: 0,
  concealmentPercent: null,
  audioLevelInbound: null,
  audioLevelOutbound: null,
  availableOutgoingBitrateKbps: null,
  route: 'UNKNOWN',
  protocol: '',
  localCandidateType: '',
  remoteCandidateType: '',
  iceState: '',
  connectionState: '',
};

export interface MediaQualityMonitorDeps {
  timers: SipTimerPort;
  now: () => number;
  getActiveStream: () => MediaStream | null;
  onQuality: (quality: WebRtcMediaQuality) => void;
  onWarning: (message: string) => void;
  logger: SipLogger;
}

type PreviousSample = {
  at: number;
  bytesReceived: number;
  bytesSent: number;
};

export class MediaQualityMonitor {
  private readonly timers: SipTimerPort;
  private readonly now: () => number;
  private readonly getActiveStream: () => MediaStream | null;
  private readonly onQuality: (quality: WebRtcMediaQuality) => void;
  private readonly onWarning: (message: string) => void;
  private readonly logger: SipLogger;
  private snapshot: WebRtcMediaQuality = { ...EMPTY_MEDIA_QUALITY };
  private interval: number | null = null;
  private session: any = null;
  private generation = 0;
  private previous: PreviousSample | null = null;
  private startedAt = 0;
  private lastBytesSent = 0;
  private warningShown = false;
  private disposed = false;

  constructor(deps: MediaQualityMonitorDeps) {
    this.timers = deps.timers;
    this.now = deps.now;
    this.getActiveStream = deps.getActiveStream;
    this.onQuality = deps.onQuality;
    this.onWarning = deps.onWarning;
    this.logger = deps.logger;
  }

  start(session: any): void {
    if (this.disposed) return;
    this.stop(false);
    this.session = session;
    this.generation += 1;
    const generation = this.generation;
    this.startedAt = this.now();
    this.previous = null;
    this.lastBytesSent = 0;
    this.warningShown = false;
    this.publish({
      ...EMPTY_MEDIA_QUALITY,
      active: true,
      updatedAt: this.startedAt,
    });

    void this.inspect(session, generation);
    this.interval = this.timers.setInterval(() => {
      void this.inspect(session, generation);
    }, 2_000);
  }

  stop(publishInactive = false): void {
    this.generation += 1;
    this.session = null;
    this.previous = null;
    if (this.interval !== null) {
      this.timers.clearInterval(this.interval);
      this.interval = null;
    }

    if (publishInactive && this.snapshot.active) {
      this.publish({ ...this.snapshot, active: false });
    }
  }

  getSnapshot(): WebRtcMediaQuality {
    return { ...this.snapshot };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stop(false);
  }

  private isCurrent(session: any, generation: number): boolean {
    return !this.disposed
      && this.session === session
      && this.generation === generation;
  }

  private publish(quality: WebRtcMediaQuality): void {
    this.snapshot = { ...quality };
    this.onQuality(this.getSnapshot());
  }

  private async inspect(session: any, generation: number): Promise<void> {
    const connection: RTCPeerConnection | undefined = session?.connection;
    if (
      !this.isCurrent(session, generation)
      || !connection
      || connection.connectionState === 'closed'
    ) {
      return;
    }

    try {
      const report = await connection.getStats();
      if (!this.isCurrent(session, generation)) return;

      const byId = new Map<string, any>();
      const all: any[] = [];
      report.forEach(item => {
        const stat = item as any;
        byId.set(String(stat.id || ''), stat);
        all.push(stat);
      });

      const audioOutbound = all.find(stat => (
        stat.type === 'outbound-rtp'
        && !stat.isRemote
        && (stat.kind === 'audio' || stat.mediaType === 'audio')
      ));
      const audioInbound = all.find(stat => (
        stat.type === 'inbound-rtp'
        && !stat.isRemote
        && (stat.kind === 'audio' || stat.mediaType === 'audio')
      ));
      const remoteInbound = (
        audioOutbound?.remoteId ? byId.get(audioOutbound.remoteId) : null
      ) || all.find(stat => (
        stat.type === 'remote-inbound-rtp'
        && (stat.kind === 'audio' || stat.mediaType === 'audio')
      ));
      const mediaSource = all.find(stat => (
        stat.type === 'media-source' && stat.kind === 'audio'
      ));

      const transport = all.find(stat => (
        stat.type === 'transport' && stat.selectedCandidatePairId
      ));
      const selectedPair = (
        transport?.selectedCandidatePairId
          ? byId.get(transport.selectedCandidatePairId)
          : null
      ) || all.find(stat => (
        stat.type === 'candidate-pair'
        && stat.state === 'succeeded'
        && (stat.selected || stat.nominated)
      ));
      const localCandidate = selectedPair?.localCandidateId
        ? byId.get(selectedPair.localCandidateId)
        : null;
      const remoteCandidate = selectedPair?.remoteCandidateId
        ? byId.get(selectedPair.remoteCandidateId)
        : null;

      const codecStat = (
        audioOutbound?.codecId ? byId.get(audioOutbound.codecId) : null
      ) || (
        audioInbound?.codecId ? byId.get(audioInbound.codecId) : null
      );
      const codec = String(codecStat?.mimeType || '')
        .replace(/^audio\//i, '')
        .toUpperCase();

      const bytesSent = Number(audioOutbound?.bytesSent || 0);
      const bytesReceived = Number(audioInbound?.bytesReceived || 0);
      const packetsSent = Number(audioOutbound?.packetsSent || 0);
      const packetsReceived = Number(audioInbound?.packetsReceived || 0);
      const packetsLost = Math.max(0, Number(audioInbound?.packetsLost || 0));
      const packetsTotal = packetsReceived + packetsLost;
      const packetLossPercent = packetsTotal > 0
        ? (packetsLost / packetsTotal) * 100
        : null;
      const outboundPacketsLost = Math.max(
        0,
        Number(remoteInbound?.packetsLost || 0),
      );
      const remoteFractionLost = Number(remoteInbound?.fractionLost);
      const outboundPacketLossPercent = Number.isFinite(remoteFractionLost)
        ? Math.max(0, remoteFractionLost) * 100
        : packetsSent > 0
          ? Math.min(100, (outboundPacketsLost / packetsSent) * 100)
          : null;
      const jitter = Number(audioInbound?.jitter);
      const outboundJitter = Number(remoteInbound?.jitter);
      const rttSeconds = Number(
        selectedPair?.currentRoundTripTime ?? remoteInbound?.roundTripTime,
      );
      const jitterBufferDelay = Number(audioInbound?.jitterBufferDelay || 0);
      const jitterBufferEmittedCount = Number(
        audioInbound?.jitterBufferEmittedCount || 0,
      );
      const concealedSamples = Math.max(
        0,
        Number(audioInbound?.concealedSamples || 0),
      );
      const totalSamplesReceived = Math.max(
        0,
        Number(audioInbound?.totalSamplesReceived || 0),
      );

      const now = this.now();
      let inboundBitrateKbps: number | null = null;
      let outboundBitrateKbps: number | null = null;
      if (this.previous && now > this.previous.at) {
        const seconds = (now - this.previous.at) / 1_000;
        inboundBitrateKbps = Math.max(
          0,
          ((bytesReceived - this.previous.bytesReceived) * 8) / seconds / 1_000,
        );
        outboundBitrateKbps = Math.max(
          0,
          ((bytesSent - this.previous.bytesSent) * 8) / seconds / 1_000,
        );
      }
      this.previous = { at: now, bytesReceived, bytesSent };

      const localType = String(localCandidate?.candidateType || '');
      const remoteType = String(remoteCandidate?.candidateType || '');
      const route: WebRtcMediaQuality['route'] = (
        localType === 'relay' || remoteType === 'relay'
      )
        ? 'TURN_RELAY'
        : localType === 'srflx' || remoteType === 'srflx'
          ? 'STUN'
          : localType || remoteType
            ? 'DIRECT'
            : 'UNKNOWN';
      const audioLevel = Math.max(0, Number(mediaSource?.audioLevel || 0));
      const totalAudioEnergy = Math.max(
        0,
        Number(mediaSource?.totalAudioEnergy || 0),
      );
      const inboundAudioLevel = Number(audioInbound?.audioLevel);
      const availableOutgoingBitrate = Number(
        selectedPair?.availableOutgoingBitrate,
      );

      this.publish({
        active: true,
        updatedAt: now,
        codec,
        clockRate: this.finiteNumber(codecStat?.clockRate),
        channels: this.finiteNumber(codecStat?.channels),
        inboundBitrateKbps,
        outboundBitrateKbps,
        packetsReceived,
        packetsSent,
        packetsLost,
        outboundPacketsLost,
        packetLossPercent,
        outboundPacketLossPercent,
        jitterMs: Number.isFinite(jitter) ? jitter * 1_000 : null,
        outboundJitterMs: Number.isFinite(outboundJitter)
          ? outboundJitter * 1_000
          : null,
        rttMs: Number.isFinite(rttSeconds) ? rttSeconds * 1_000 : null,
        jitterBufferMs: jitterBufferDelay > 0 && jitterBufferEmittedCount > 0
          ? (jitterBufferDelay / jitterBufferEmittedCount) * 1_000
          : null,
        concealedSamples,
        totalSamplesReceived,
        concealmentPercent: totalSamplesReceived > 0
          ? (concealedSamples / totalSamplesReceived) * 100
          : null,
        audioLevelInbound: Number.isFinite(inboundAudioLevel)
          ? inboundAudioLevel
          : null,
        audioLevelOutbound: audioLevel,
        availableOutgoingBitrateKbps: Number.isFinite(availableOutgoingBitrate)
          ? availableOutgoingBitrate / 1_000
          : null,
        route,
        protocol: String(
          localCandidate?.protocol || remoteCandidate?.protocol || '',
        ).toUpperCase(),
        localCandidateType: localType,
        remoteCandidateType: remoteType,
        iceState: String(connection.iceConnectionState || '').toLowerCase(),
        connectionState: String(connection.connectionState || '').toLowerCase(),
      });

      this.logger.debug(
        '[telephony] v15.6.4 media quality',
        this.getSnapshot(),
      );

      if (
        now - this.startedAt > 8_000
        && !this.warningShown
        && bytesSent > this.lastBytesSent
        && audioLevel === 0
        && totalAudioEnergy === 0
      ) {
        this.warningShown = true;
        const track = this.getActiveStream()?.getAudioTracks()[0];
        this.onWarning(
          track?.muted
            ? 'Микрофон отключён устройством. Проверьте гарнитуру.'
            : 'Микрофон передаёт нулевой уровень. Выберите другое устройство в блоке «Гарнитура».',
        );
      }

      this.lastBytesSent = bytesSent;
    } catch (error) {
      if (this.isCurrent(session, generation)) {
        this.logger.warn('[telephony] media health check failed', error);
      }
    }
  }

  private finiteNumber(value: unknown): number | null {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }
}
